import React, { Component } from "react";
import axios from "axios";
import PropTypes from "prop-types";

/**
 * BulkEmailCampaign - React CLASS component
 * - Sends a bulk email request to POST /api/marketing/bulk-email
 * - Expects an API that accepts { to: string[], subject: string, body: string, cc?: string[], bcc?: string[] }
 * - Uses a textarea for comma/newline separated recipients, subject input and HTML body textarea.
 *
 * Usage:
 * <BulkEmailCampaign apiBaseUrl="/api/marketing" />
 */
export default class BulkEmailCampaign extends Component {
  constructor(props) {
    super(props);
    this.state = {
      recipientsText: "", // comma or newline separated
      ccText: "",
      bccText: "",
      subject: "",
      body: "",
      isHtml: true,
      sending: false,
      successMessage: "",
      errorMessage: "",
      validationErrors: [],
      previewOpen: false,
    };
  }

  parseRecipients = (text) => {
    if (!text) return [];
    // Split by commas/newlines/semicolons and trim
    const parts = text
      .split(/[,;\n\r]+/)
      .map((p) => p.trim())
      .filter((p) => p.length > 0);
    // Deduplicate preserving order
    return Array.from(new Set(parts));
  };

  validate = () => {
    const errors = [];
    const to = this.parseRecipients(this.state.recipientsText);
    if (!to.length) errors.push("At least one recipient is required.");
    if (!this.state.subject || !this.state.subject.trim())
      errors.push("Subject is required.");
    if (!this.state.body || !this.state.body.trim())
      errors.push("Body is required.");
    // Basic email sanity-check for first few recipients
    const emailRegex = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
    const invalid = to.filter((t) => !emailRegex.test(t));
    if (invalid.length)
      errors.push(
        `Invalid email addresses detected: ${invalid.slice(0, 5).join(", ")}${
          invalid.length > 5 ? " (and more)" : ""
        }`
      );
    this.setState({ validationErrors: errors });
    return errors.length === 0;
  };

  handleSend = async (e) => {
    e && e.preventDefault();
    this.setState({ successMessage: "", errorMessage: "" });

    if (!this.validate()) return;

    const to = this.parseRecipients(this.state.recipientsText);
    const cc = this.parseRecipients(this.state.ccText);
    const bcc = this.parseRecipients(this.state.bccText);

    const payload = {
      to,
      subject: this.state.subject,
      body: this.state.body,
      cc: cc.length ? cc : undefined,
      bcc: bcc.length ? bcc : undefined,
      isHtml: this.state.isHtml,
    };

    this.setState({ sending: true });
    try {
      const url = `${this.props.apiBaseUrl.replace(/\/$/, "")}/bulk-email`;
      const resp = await axios.post(url, payload, {
        timeout: this.props.timeout,
      });
      // Expect 202 Accepted or 200
      if (resp.status >= 200 && resp.status < 300) {
        this.setState({
          successMessage:
            resp.data?.message || "Emails queued/sent successfully.",
          errorMessage: "",
        });
      } else {
        this.setState({ errorMessage: `Unexpected response: ${resp.status}` });
      }
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data || err.message || String(err);
      this.setState({ errorMessage: `Error sending: ${msg}` });
    } finally {
      this.setState({ sending: false });
    }
  };

  handleChange = (field) => (e) =>
    this.setState({
      [field]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    });

  togglePreview = () => this.setState((s) => ({ previewOpen: !s.previewOpen }));

  renderValidation = () => {
    if (!this.state.validationErrors.length) return null;
    return (
      <div className="alert alert-warning" role="alert">
        <strong>Fix these before sending:</strong>
        <ul className="mb-0">
          {this.state.validationErrors.map((v, i) => (
            <li key={i}>{v}</li>
          ))}
        </ul>
      </div>
    );
  };

  render() {
    return (
      <div className="card">
        <div className="card-body">
          <h5 className="card-title">Bulk Email Campaign</h5>

          {this.state.successMessage && (
            <div className="alert alert-success">
              {this.state.successMessage}
            </div>
          )}
          {this.state.errorMessage && (
            <div className="alert alert-danger">{this.state.errorMessage}</div>
          )}

          {this.renderValidation()}

          <form onSubmit={this.handleSend}>
            <div className="mb-3">
              <label className="form-label">
                Recipients (comma, semicolon or newline separated)
              </label>
              <textarea
                className="form-control"
                rows={3}
                value={this.state.recipientsText}
                onChange={this.handleChange("recipientsText")}
                placeholder="alice@example.com, bob@example.com"
              />
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">CC (optional)</label>
                <input
                  className="form-control"
                  value={this.state.ccText}
                  onChange={this.handleChange("ccText")}
                  placeholder="cc1@example.com"
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">BCC (optional)</label>
                <input
                  className="form-control"
                  value={this.state.bccText}
                  onChange={this.handleChange("bccText")}
                  placeholder="bcc1@example.com"
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">Subject</label>
              <input
                className="form-control"
                value={this.state.subject}
                onChange={this.handleChange("subject")}
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Body</label>
              <textarea
                className="form-control"
                rows={8}
                value={this.state.body}
                onChange={this.handleChange("body")}
              />
              <div className="form-text">
                You can paste HTML when "Is HTML" is checked.
              </div>
            </div>

            <div className="form-check form-switch mb-3">
              <input
                className="form-check-input"
                type="checkbox"
                id="isHtml"
                checked={this.state.isHtml}
                onChange={this.handleChange("isHtml")}
              />
              <label className="form-check-label" htmlFor="isHtml">
                Is HTML
              </label>
            </div>

            <div className="d-flex gap-2">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={this.state.sending}
              >
                {this.state.sending ? "Sending..." : "Send Emails"}
              </button>

              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={this.togglePreview}
              >
                {this.state.previewOpen ? "Hide Preview" : "Preview"}
              </button>

              <button
                type="button"
                className="btn btn-outline-danger"
                onClick={() =>
                  this.setState({
                    recipientsText: "",
                    ccText: "",
                    bccText: "",
                    subject: "",
                    body: "",
                    validationErrors: [],
                    successMessage: "",
                    errorMessage: "",
                  })
                }
              >
                Clear
              </button>
            </div>
          </form>

          {this.state.previewOpen && (
            <div className="mt-3">
              <h6>Preview</h6>
              <div className="border p-3" style={{ minHeight: 120 }}>
                {this.state.isHtml ? (
                  // eslint-disable-next-line react/no-danger
                  <div dangerouslySetInnerHTML={{ __html: this.state.body }} />
                ) : (
                  <pre style={{ whiteSpace: "pre-wrap" }}>
                    {this.state.body}
                  </pre>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

BulkEmailCampaign.propTypes = {
  apiBaseUrl: PropTypes.string, // e.g. '/api/marketing'
  timeout: PropTypes.number,
};

BulkEmailCampaign.defaultProps = {
  apiBaseUrl: "/api/marketing",
  timeout: 120000,
};
